import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

interface DeleteConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    content?: string;
    loading?: boolean;
    itemToDelete?: string;
    /** Confirm button text (default: Delete) — e.g. Remove / Disconnect / Continue. */
    confirmLabel?: string;
    /** The red "cannot be undone" alert (default on); off for confirmations that are not deletions. */
    showWarning?: boolean;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    content,
    loading = false,
    itemToDelete,
    confirmLabel,
    showWarning = true
}) => {
    const { t } = useTranslation(namespace);

    const displayTitle = title || t('deleteConfirmationDialog.title');
    const displayContent = content || t('deleteConfirmationDialog.content');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {displayTitle}
            </DialogTitle>
            <DialogContent>
                {showWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('deleteConfirmationDialog.warning')}
                    </Alert>
                )}
                <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {itemToDelete ? (
                        <Box>
                            <Trans
                                ns={namespace}
                                i18nKey="deleteConfirmationDialog.confirmDeleteItem"
                                values={{ item: itemToDelete }}
                                components={{
                                    b: <Box component="span" sx={{ fontWeight: 700 }} />
                                }}
                            />
                        </Box>
                    ) : displayContent}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={loading}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="error"
                    disabled={loading}
                    sx={{
                        minWidth: '100px',
                        height: '36px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (confirmLabel ?? t('common.delete'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 
